import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppShell from './AppShell';
import WeChatOAuthQrDisplay from './WeChatOAuthQrDisplay';
import { API_BASE_URL } from '../config/api';
import { wechatLoginLog } from '../utils/wechatLoginLog';
import { useWeChatQrPollSession } from '../hooks/useWeChatQrPollSession';
import { ChevronLeft } from 'lucide-react';
import './Login.css';
import './LoginMethod.css';

/**
 * 非微信环境：展示公众号网页授权链接的二维码（与微信内 H5 同一套 oauth2，非 qrconnect）
 */
const WeChatQrLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [pollingKey, setPollingKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState<string | null>(null);

  useWeChatQrPollSession(pollingKey, {
    onExpired: () => {
      setFetchErr((prev) => prev || '等待超时，请返回后重新扫码');
    },
  });

  useEffect(() => {
    let cancelled = false;
    wechatLoginLog('wechat_qr_page_mount', { apiBase: API_BASE_URL });
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/auth/wechat/start?mode=qr`);
        const j = await r.json();
        if (cancelled) return;
        wechatLoginLog('wechat_qr_page_start_response', {
          httpOk: r.ok,
          httpStatus: r.status,
          success: Boolean(j.success),
          hasUrl: Boolean(j.data?.url),
        });
        if (j.success && j.data?.url) {
          setAuthUrl(j.data.url as string);
          const pk = typeof (j.data as { pollingKey?: string }).pollingKey === 'string' ? (j.data as { pollingKey: string }).pollingKey : null;
          setPollingKey(pk);
        } else {
          setFetchErr((j as { error?: string }).error || '无法加载微信登录');
        }
      } catch (e) {
        if (!cancelled) {
          setFetchErr('网络错误');
          wechatLoginLog('wechat_qr_page_fetch_fail', { err: e instanceof Error ? e.message : String(e) });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppShell centerBody>
      <div className="login-box wechat-qr-wrap">
        <div className="login-flow-back-row">
          <button type="button" className="login-method-back login-method-back--top" onClick={() => navigate('/sign-in', { state: location.state })}>
            <ChevronLeft size={18} strokeWidth={2.25} aria-hidden />
            <span>返回</span>
          </button>
        </div>
        <h2>微信扫码登录</h2>
        <WeChatOAuthQrDisplay authUrl={authUrl} loading={loading} fetchError={fetchErr} />
        {pollingKey && !fetchErr ? (
          <p className="wechat-done-msg" style={{ marginTop: 12 }}>
            在手机微信上确认授权后，本页将自动进入首页
          </p>
        ) : null}
        <button type="button" className="login-method-back" onClick={() => navigate('/sign-in', { state: location.state })}>
          返回选择登录方式
        </button>
      </div>
    </AppShell>
  );
};

export default WeChatQrLogin;
